import {
  Component,
  forwardRef,
  lazy,
  Suspense,
  useEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';

import GlobalSearchFallback from './GlobalSearchFallback';

// GlobalSearch(자동완성 다이얼로그·최근 검색어 등 검색 UI 전체)는 부피가 커서
// GNB가 정적 import하면 First Load 공유 청크(_app)가 그만큼 커진다.
// 실제 렌더 시점에만 로드되도록 지연 import하여 별도 청크로 분리한다.
// (과거엔 recoil 격리 목적도 있었으나 Recoil→Context 교체로 recoil은 더 이상 없음)
const GlobalSearch = lazy(() => import('./index'));

type GlobalSearchProps = ComponentProps<typeof GlobalSearch>;

// lazy 청크 로드가 실패하면(네트워크·CDN 장애) 에러가 Suspense를 통과해 상위로 전파되어
// Header가 통째로 unmount될 수 있다. 헤더는 전 페이지 공통이라 화이트스크린으로 이어지므로,
// 검색은 필수 기능이 아닌 만큼 실패 시 조용히 숨겨(fallback) GNB를 보호한다.
class GlobalSearchErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const LazyGlobalSearch = forwardRef<HTMLInputElement, GlobalSearchProps>(function LazyGlobalSearch(
  props,
  ref,
) {
  // Header는 SSR로 렌더된다. lazy 컴포넌트는 서버에서 fallback을 출력하므로,
  // 클라이언트 마운트 전(hydration 시점)까지 서버와 동일하게 fallback만 렌더한다.
  // 마운트 후에만 lazy를 붙여, hydration 도중 Suspense 경계가 업데이트를 받아
  // client 렌더로 전환되며 터지는 React #421(hydration 경합)을 방지한다.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const fallback = <GlobalSearchFallback isShowSearchInput={props.isShowSearchInput} />;

  if (!isMounted) {
    return fallback;
  }

  return (
    <GlobalSearchErrorBoundary fallback={fallback}>
      <Suspense fallback={fallback}>
        <GlobalSearch {...props} ref={ref} />
      </Suspense>
    </GlobalSearchErrorBoundary>
  );
});

export default LazyGlobalSearch;
